'use client';

import { useIsCheckoutBusy } from '@/hooks/useCheckout';
import { usePaymentMethodAvailability } from '@/hooks/usePaymentMethodAvailability';
import { ACCOUNT_METADATA } from '@/lib/auth/metadata';
import { fetchCheckoutFromBFF } from '@/lib/checkout/api';
import { devWarn } from '@/lib/utils/devLog';
import { getCheckoutCookie } from '@/lib/checkout/cookies.client';
import { useCheckoutLifecycle } from '@/lib/checkout/hooks/useCheckoutLifecycle';
import { resolveLineDisplayPrice } from '@/lib/checkout/resolveLineDisplayPrice';
import { getChannelFromLocale } from '@/lib/configuration/channels';
import {
  checkoutActions,
  checkoutState,
  getChannelSnapshot,
  initCheckoutSubscriptions,
  saveToChannelSnapshot,
  setActiveChannel,
  setSkipPersistence,
} from '@/states/checkout';
import { modalActions } from '@/states/modalState';
import { useTranslations } from 'next-intl';
import { useParams, usePathname } from 'next/navigation';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useAnnounce } from '@/providers/liveRegionProvider';

/**
 * Cart UI Context - for managing cart drawer visibility
 * Separate from checkout data state (valtio) per FINAL_PLAN.md
 */
interface CartUIContextType {
  /** Whether the cart drawer is open */
  isDrawerOpen: boolean;
  /** Open the cart drawer */
  openDrawer: () => void;
  /** Close the cart drawer */
  closeDrawer: () => void;
  /** Toggle the cart drawer */
  toggleDrawer: () => void;
}

const CartUIContext = createContext<CartUIContextType | null>(null);

interface CartProviderProps {
  children: ReactNode;
}

/**
 * CartProvider - manages cart UI state (drawer open/close)
 * Also initializes checkout state from cookie on mount
 *
 * @example
 * ```tsx
 * // In layout
 * <CartProvider>
 *   <Header />
 *   <main>{children}</main>
 *   <CartDrawer />
 * </CartProvider>
 *
 * // In Header
 * function CartButton() {
 *   const { toggleDrawer } = useCartUI();
 *   const count = useCartCount();
 *   return <button onClick={toggleDrawer}>Cart ({count})</button>;
 * }
 * ```
 */
export function CartProvider({ children }: CartProviderProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const params = useParams();
  const pathname = usePathname();

  // Pre-fetch payment method availability on mount
  // This ensures the cache is populated before the cart drawer opens
  usePaymentMethodAvailability();

  // Announce checkout busy state to screen readers (WCAG 4.1.3)
  const announce = useAnnounce();
  const t = useTranslations('checkout');
  const isCheckoutBusy = useIsCheckoutBusy();
  const prevBusyRef = useRef(false);

  useEffect(() => {
    if (isCheckoutBusy && !prevBusyRef.current) {
      announce(t('cart.updatingBag'));
    }
    prevBusyRef.current = isCheckoutBusy;
  }, [isCheckoutBusy, announce, t]);

  // Get current channel from locale for per-country cart isolation
  const locale = (params?.lang as string) || 'us-en';
  // ponytail: set locale at layout mount so setErrors localizes bag banners before useCheckout runs
  if (checkoutState.locale !== locale) {
    checkoutActions.setLocale(locale);
  }
  const channelSlug = getChannelFromLocale(locale);

  // Use the lifecycle hook to get restore function
  const { restore } = useCheckoutLifecycle({ locale });

  // Store restore in a ref to avoid useEffect dependency issues
  const restoreRef = useRef(restore);
  restoreRef.current = restore;

  // Track if we've restored for this channel
  const restoredChannelsRef = useRef<Set<string>>(new Set());

  // Check if we're on a checkout page (requires immediate restore)
  const isCheckoutPage = pathname?.includes('/checkout');

  /**
   * LAZY RESTORE WITH PERSISTENCE:
   *
   * Checkout snapshots are PERSISTED to localStorage, so on page load:
   * 1. If we have valid cached data (< 15 min old) - use it, NO fetch
   * 2. If cache is stale but exists - use it for immediate display, fetch in background
   * 3. If no cache but cookie exists - fetch to populate cart
   * 4. If no cache and no cookie - nothing to do
   *
   * This eliminates unnecessary API calls on every page load.
   */

  // Initialize channel context on mount/channel change
  useEffect(() => {
    // Skip persistence during channel switch
    setSkipPersistence(true);

    // Set the active channel
    setActiveChannel(channelSlug);
    checkoutState.channelSlug = channelSlug;

    // Check if we have cached data for this channel (hydrates from localStorage)
    const cached = getChannelSnapshot(channelSlug);
    const hasCheckoutCookie = !!getCheckoutCookie(channelSlug);

    console.log('[CartProvider DEBUG] useEffect:', {
      channelSlug,
      hasCheckoutCookie,
      cachedHasCheckout: !!cached.checkout,
      cachedLinesCount: cached.checkout?.lines?.length || 0,
      cachedId: cached.id?.slice(0, 8),
      cachedNeedsRefresh: cached.needsRefresh,
    });

    if (cached.checkout) {
      console.log('[CartProvider DEBUG] Using cached checkout data');
      // Use cached data immediately (from localStorage)
      checkoutState.id = cached.id;
      checkoutState.checkout = cached.checkout;
      checkoutState.email = cached.email;
      checkoutState.deliveryMethodId = cached.deliveryMethodId;

      // Restore delivery mode so boutique pickup state survives page reload.
      if (cached.deliveryMode && !checkoutState.deliveryMode) {
        checkoutState.deliveryMode = cached.deliveryMode;
      }
      if (cached.boutiqueExtRef && !checkoutState.boutiqueExtRef) {
        checkoutState.boutiqueExtRef = cached.boutiqueExtRef;
      }

      // Restore addresses from Saleor checkout so the delivery form
      // can rehydrate after a long idle or page reload.
      // Extract personalTitle from address metadata so the title field
      // survives page reload / cache restore (billing has no separate
      // rehydration effect unlike shipping).
      if (cached.checkout.shippingAddress && !checkoutState.shippingAddress) {
        const sa = cached.checkout.shippingAddress;
        checkoutState.shippingAddress = {
          firstName: sa.firstName,
          lastName: sa.lastName,
          companyName: sa.companyName,
          streetAddress1: sa.streetAddress1,
          streetAddress2: sa.streetAddress2,
          city: sa.city,
          postalCode: sa.postalCode,
          country: sa.country.code,
          countryArea: sa.countryArea,
          phone: sa.phone,
          personalTitle: sa.metadata?.find((m) => m.key === ACCOUNT_METADATA.personalTitle)?.value,
        };
      }
      if (cached.checkout.billingAddress && !checkoutState.billingAddress) {
        const ba = cached.checkout.billingAddress;
        checkoutState.billingAddress = {
          firstName: ba.firstName,
          lastName: ba.lastName,
          companyName: ba.companyName,
          streetAddress1: ba.streetAddress1,
          streetAddress2: ba.streetAddress2,
          city: ba.city,
          postalCode: ba.postalCode,
          country: ba.country.code,
          countryArea: ba.countryArea,
          phone: ba.phone,
          personalTitle: ba.metadata?.find((m) => m.key === ACCOUNT_METADATA.personalTitle)?.value,
        };
      }

      // Sync lines from checkout
      checkoutState.lines =
        cached.checkout?.lines?.map((line) => {
          const { amount, currency } = resolveLineDisplayPrice(line, channelSlug);
          return {
            id: line.id,
            variantId: line.variant?.id || '',
            sku: line.variant?.sku || '',
            name: line.variant?.product?.name || '',
            price: amount,
            currency,
            quantity: line.quantity,
            image: line.variant?.product?.thumbnail?.url,
            productType: line.variant?.product?.productType?.slug,
          };
        }) || [];
      checkoutState.isInitializing = false;
      restoredChannelsRef.current.add(channelSlug);
      console.log('[CartProvider DEBUG] Set lines from cache:', checkoutState.lines.length);

      // If cache is stale (needsRefresh), fetch in background to update
      if (cached.needsRefresh) {
        console.log('[CartProvider DEBUG] Cache is stale, fetching in background');
        restoreRef.current().catch((err) => {
          console.log('[CartProvider DEBUG] Background restore failed:', err);
        });
      }
    } else if (hasCheckoutCookie) {
      console.log('[CartProvider DEBUG] No cache but have cookie, fetching...');
      // We have a cookie but no cache (e.g., first load after clearing localStorage)
      // Need to fetch to populate cart
      checkoutState.id = getCheckoutCookie(channelSlug);
      checkoutState.checkout = null;
      checkoutState.lines = [];
      checkoutState.isInitializing = true;

      // Fetch to populate cart count and cache
      restoredChannelsRef.current.add(channelSlug);
      restoreRef
        .current()
        .then((result) => {
          console.log('[CartProvider DEBUG] Restore completed:', {
            hasCheckout: !!result.checkout,
            linesCount: result.checkout?.lines?.length || 0,
          });
        })
        .finally(() => {
          checkoutState.isInitializing = false;
        });
    } else {
      console.log('[CartProvider DEBUG] No checkout for this channel');
      // No checkout for this channel
      checkoutState.id = null;
      checkoutState.checkout = null;
      checkoutState.lines = [];
      checkoutState.email = null;
      checkoutState.shippingAddress = null;
      checkoutState.billingAddress = null;
      checkoutState.deliveryMethodId = null;
      checkoutState.isInitializing = false;
    }

    setSkipPersistence(false);

    // Initialize subscriptions (only runs once due to internal guard)
    initCheckoutSubscriptions();
  }, [channelSlug]); // Only depend on channelSlug, not restore

  // Cookie sync: Detect when checkout cookie changes (e.g., after login sets it server-side)
  // This handles the case where login restores a checkout but client state is stale
  useEffect(() => {
    // Flag to stop polling after logout
    let isCleared = false;

    const syncCheckout = (checkoutId: string) => {
      if (isCleared) return; // Don't sync if we've been cleared

      console.log('[CartProvider DEBUG] Syncing checkout:', checkoutId.slice(0, 20));

      // Update state with new checkout ID and CLEAR stale data
      // This prevents using old line IDs that don't exist in the new checkout
      checkoutState.id = checkoutId;
      checkoutState.checkout = null;
      checkoutState.lines = [];
      checkoutState.isInitializing = true;

      // Fetch the checkout data
      restoreRef
        .current({ force: true, checkoutId })
        .then((result) => {
          console.log('[CartProvider DEBUG] Checkout sync completed:', {
            hasCheckout: !!result.checkout,
            linesCount: result.checkout?.lines?.length || 0,
          });
        })
        .finally(() => {
          checkoutState.isInitializing = false;
        });
    };

    const checkCookieSync = () => {
      if (isCleared) return; // Don't sync if we've been cleared

      const cookieId = getCheckoutCookie(channelSlug);
      const stateId = checkoutState.id;

      // If cookie exists but differs from state, we need to sync
      if (cookieId && cookieId !== stateId) {
        console.log('[CartProvider DEBUG] Cookie mismatch detected, syncing...');
        syncCheckout(cookieId);
      }
    };

    // Listen for checkout-restored event from login flow
    // This is the most reliable way to sync immediately after login
    const handleCheckoutRestored = (event: Event) => {
      const customEvent = event as CustomEvent<{ checkoutId: string; channelSlug: string }>;
      const { checkoutId, channelSlug: eventChannelSlug } = customEvent.detail;

      console.log('[CartProvider DEBUG] checkout-restored event received:', {
        checkoutId: checkoutId?.slice(0, 20),
        eventChannelSlug,
        currentChannelSlug: channelSlug,
        wasCleared: isCleared,
      });

      // Reset the cleared flag - user is logging back in, we should sync
      if (isCleared) {
        console.log('[CartProvider DEBUG] Resetting isCleared flag for new login');
        isCleared = false;
      }

      // Only sync if the event is for the current channel
      if (eventChannelSlug === channelSlug && checkoutId) {
        syncCheckout(checkoutId);
      }
    };

    // Poll briefly after mount to catch cookie set by login response
    // Defined early so handleCheckoutCleared can reference it
    const pollId = setInterval(checkCookieSync, 1000);
    const stopPollingId = setTimeout(() => clearInterval(pollId), 5000);

    // Listen for checkout-cleared event from logout flow
    // This stops polling and ensures we don't restore after logout
    const handleCheckoutCleared = () => {
      console.log('[CartProvider DEBUG] checkout-cleared event received, stopping sync');
      isCleared = true;
      // Clear all intervals to stop any pending polls
      clearInterval(pollId);
      clearTimeout(stopPollingId);
    };

    window.addEventListener('checkout-restored', handleCheckoutRestored);
    window.addEventListener('checkout-cleared', handleCheckoutCleared);

    // Check immediately on mount/re-render
    checkCookieSync();

    // Also check after a short delay (for router.refresh() timing)
    const timeoutId = setTimeout(checkCookieSync, 500);

    // Also check on visibility change (when user returns to tab after login redirect)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkCookieSync();
      }
    };

    // And on focus (handles SPA navigation after login)
    const handleFocus = () => {
      checkCookieSync();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(stopPollingId);
      clearInterval(pollId);
      window.removeEventListener('checkout-restored', handleCheckoutRestored);
      window.removeEventListener('checkout-cleared', handleCheckoutCleared);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [channelSlug]);

  // Force refresh: Fetch fresh data on checkout pages
  useEffect(() => {
    if (!isCheckoutPage) return;
    if (!checkoutState.id) return;

    // On checkout pages, always get fresh data (but still use cache for immediate render)
    restoredChannelsRef.current.add(channelSlug);
    restoreRef.current({ force: true }).finally(() => {
      checkoutState.isInitializing = false;
    });
  }, [isCheckoutPage, channelSlug]);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
    modalActions.lockScroll();

    if (!checkoutState.id) return;

    if (!restoredChannelsRef.current.has(channelSlug)) {
      // First open: full restore (fetch + cache)
      restoredChannelsRef.current.add(channelSlug);
      restoreRef.current().finally(() => {
        checkoutState.isInitializing = false;
      });
    } else {
      // Subsequent opens: silent background refresh so drawer never shows stale
      // data. We fetch directly instead of going through restore() or refresh()
      // because both set processing/initializing flags that block UI interaction
      // (disabled buttons, spinner) — defeating "background" intent.
      const id = checkoutState.id;
      if (id) {
        const versionAtFetchStart = checkoutState.mutationVersion;
        fetchCheckoutFromBFF(id, locale)
          .then((result) => {
            if (!result.checkout) return;
            // Drop stale response if any cart mutation (add/remove/update)
            // occurred after this fetch started — the mutation supplies its
            // own fresh data and this response is now outdated.
            if (checkoutState.mutationVersion !== versionAtFetchStart) return;
            // Drop if checkout was cleared or replaced while fetch was in flight
            if (checkoutState.id !== id) return;

            checkoutActions.updateCheckout(result.checkout);
            saveToChannelSnapshot(channelSlug, {
              id,
              checkout: result.checkout,
              email: result.checkout.email || null,
              deliveryMethodId: result.checkout.deliveryMethod?.id || null,
            });
          })
          .catch((err) => {
            devWarn('[CartProvider] Background refresh failed:', err);
          });
      }
    }
  }, [channelSlug, locale]);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    // Use centralized scroll unlock
    modalActions.unlockScroll();
  }, []);

  const toggleDrawer = useCallback(() => {
    setIsDrawerOpen((prev) => {
      const newState = !prev;
      if (newState) {
        modalActions.lockScroll();
      } else {
        modalActions.unlockScroll();
      }
      return newState;
    });
  }, []);

  return (
    <CartUIContext.Provider value={{ isDrawerOpen, openDrawer, closeDrawer, toggleDrawer }}>
      {children}
    </CartUIContext.Provider>
  );
}

/**
 * Hook to access cart UI state
 *
 * @throws Error if used outside CartProvider
 */
export function useCartUI(): CartUIContextType {
  const context = useContext(CartUIContext);
  if (!context) {
    throw new Error('useCartUI must be used within CartProvider');
  }
  return context;
}

/**
 * Cart UI state when a cart may not exist. `FEATURE_ECOM=false` leaves
 * CartProvider unmounted, so components that also render in catalog mode must
 * not require it — see `useAddToCart`.
 */
export function useOptionalCartUI(): CartUIContextType | null {
  return useContext(CartUIContext);
}
